import { useEnvStore } from 'store'
import { RequestService } from '../RequestService'
import type { CommonConfirmSecurityAuthType } from '../constants/security'

export enum BannerType {
  /** 首页 */
  Home = 1,
}

type BindOrChangeGoogleData = {
  googleCode: string
  type: CommonConfirmSecurityAuthType
  tokenVerify: string
  googleSecret: string
}

type BindOrChangeEmailData = {
  email: string
  emailCode: string
  type: CommonConfirmSecurityAuthType
  tokenVerify: string
}

type BindOrChangeMobileData = {
  mobile: string
  area: string
  mobileCode: string
  type: CommonConfirmSecurityAuthType
  tokenVerify: string
}

export enum RechargeWithdrawRecordType {
  /** 充值 */
  Recharge = 1,
  /** 提币 */
  Withdraw = 2,
}

export enum WithdrawAddressType {
  /** 链上转账 */
  Chain = 1,
  /** uid */
  Uid = 2,
  /** 手机号 */
  Phone = 3,
  /** 邮箱 */
  Email = 4,
  AllInner = 40,
}

type AddWithdrawAddressRequiredData = {
  itemId: ItemId
  address: string
  remark: string
  transferType: WithdrawAddressType
}

type AddWithdrawAddressData = AddWithdrawAddressRequiredData & {
  chainTag?: string
  area?: string
}

type Amount = number | string

type CheckSwapAndOutTransferData = {
  itemId: ItemId
  address: string
  arriveAmount: Amount
  amount: Amount
  swapItemId: ItemId
  hashStr: string
  transferType?: WithdrawAddressType
  chainTag?: string
  remark?: string
  bak?: string
  uecId?: string
  resultSccIds?: string
}

type SwapAndOutTransferData = {
  itemId: ItemId
  amount: Amount
  remark: string
  arriveAmount: Amount
  tokenWithdraw: string
  transferType: WithdrawAddressType
  chainTag?: string
  bak?: string
  uecId: string
  resultSccIds?: string
  area: string
  hashStr: string
  swapItemId: ItemId
  address: string
}

export enum UploadPictureType {
  /** 图片 */
  Avatar = 1,
}

type UploadPictureData = {
  file: File
  type: UploadPictureType
}

class UCenterRequest extends RequestService {
  constructor() {
    super()
  }

  protected setBaseUrl(): string {
    const envStore = useEnvStore()
    return envStore.config?.appuc || ''
  }

  async getStaticTableInfoList(data: { svIds: string }): Promise<FetchResponse<ApiResponse.StaticTableData>> {
    return this.postZip<ApiResponse.StaticTableData>('/Settings/getStaticTableInfoList', data)
  }

  /** 登录 */
  login(data: { loginName: string; tokenLogin: string }) {
    return this.post<ApiResponse.LoginResponse>('/V4/UCenter/Login/universal', data)
  }

  /** 获取静态表的一些版本信息 */
  getStaticTableVersionInfo() {
    return this.post<ApiResponse.StaticTableVersionInfo>('/Settings/getStaticTableList')
  }

  /** 获取币种汇率 */
  getOtcCoinRate(data = {}) {
    return this.post<ApiResponse.OtcCoinRate>('/Settings/otcCoinRate', data)
  }

  /**
   * 谷歌验证登录
   * @param data
   * @returns
   */
  googleVerifyLogin(data: { token: string; googleCode: string }) {
    return this.post<ApiResponse.LoginResponse>('/V3/UCenter/LoginVerify/googleVerify', data)
  }

  /**
   * 邮箱验证登录
   * @param data
   * @returns
   */
  emailVerifyLogin(data: { token: string; emailCode: string }) {
    return this.post<ApiResponse.LoginResponse>('/V3/UCenter/LoginVerify/emailVerify', data)
  }

  /**
   * 手机验证登录
   * @param data
   * @returns
   */
  mobileVerifyLogin(data: { token: string; mobileCode: string }) {
    return this.post<ApiResponse.LoginResponse>('/V3/UCenter/LoginVerify/mobileVerify', data)
  }

  /** 获取banner */
  getBanner(data: { type: BannerType }) {
    return this.post<ApiResponse.BannerItem[]>('/App/Product/bannerList', data)
  }

  /** 获取资产列表 */
  getAssetItemList() {
    return this.post<Record<ItemId, ApiResponse.AssetItem>>('/Settings/itemList')
  }

  /**
   * 获取资产列表
   * @returns
   */
  getAssetsList(data: { currency?: string; type?: string }) {
    return this.post<ApiResponse.AssetsData>('/V4/UCenter/Assets/getAssets', {
      type: data.type || 'all',
      isAll: 1,
      currency: data.currency || 'cny',
    })
  }

  /**
   * 获取用户信息
   * @returns
   */
  getUserInfo() {
    return this.post<ApiResponse.UserInfoResponse>('/V4/UCenter/User/getMergeInfo')
  }

  getUserLoginList(data: ApiRequest.PageBody) {
    return this.post<HasPageInfoResponse<ApiResponse.UserLoginList>>('/UCenter/Auth/userLoginLogList', data)
  }

  /**
   * 获取绑定邮箱的验证码
   * @param data
   * @returns
   */
  getBindEmailCode(data: { email: string }) {
    return this.post('/V4/UCenter/User/bindEmailCode', data)
  }

  /**
   * 获取换绑邮箱的验证码
   * @param data
   * @returns
   */
  getChangeEmailCode(data: { email: string }) {
    return this.post('/V4/UCenter/User/changeEmailCode', data)
  }

  /**
   * 获取绑定谷歌验证的验证码
   * @param data
   * @returns
   */
  getBindGoogleCode(data: { googleSecret: string }) {
    return this.post('/V3/UCenter/User/bindGoogleCode', data)
  }

  /**
   * 获取绑定手机的验证码
   * @param data
   * @returns
   */
  getBindMobileCode(data: { mobile: string; area: string } & GeeTestResult) {
    return this.post('/V4/UCenter/User/bindMobileCode', data)
  }

  getChangeMobileCode(data: { mobile: string; area: string } & GeeTestResult) {
    return this.post('/V4/UCenter/User/changeMobileCode', data)
  }

  /**
   * 绑定手机
   * @param data
   * @returns
   */
  bindMobile(data: BindOrChangeMobileData) {
    return this.post('/V4/UCenter/User/bindMobile', data)
  }

  /**
   * 修改手机
   * @param data
   * @returns
   */
  changeMobile(data: BindOrChangeMobileData) {
    return this.post('/V4/UCenter/User/changeMobile', data)
  }

  /**
   * 谷歌验证
   * @param data
   * @returns
   */
  safetyVerifyGoogle(data: { googleCode: string; type: CommonConfirmSecurityAuthType }) {
    return this.post<ApiResponse.SafetyAuthResponse>('/V4/UCenter/User/safetyVerifyGoogle', data)
  }

  /**
   * 邮箱验证
   * @param data
   * @returns
   */
  safetyVerifyEmail(data: { emailCode: string; type: CommonConfirmSecurityAuthType }) {
    return this.post<ApiResponse.SafetyAuthResponse>('/V4/UCenter/User/safetyVerifyEmail', data)
  }

  /**
   * 手机验证
   * @param data
   * @returns
   */
  safetyVerifyMobile(data: { mobileCode: string; type: CommonConfirmSecurityAuthType }) {
    return this.post<ApiResponse.SafetyAuthResponse>('/V4/UCenter/User/safetyVerifyMobile', data)
  }

  /**
   * 发送安全项变更短信
   * @param data
   * @returns
   */
  sendVerifyMobileCode(data: { type: CommonConfirmSecurityAuthType } & Record<string, any>) {
    return this.post('/V4/UCenter/User/verifyMobileCode', data)
  }

  /**
   * 发送安全项变更邮箱
   * @param data
   * @returns
   */
  sendVerifyEmailCode(data: { type: CommonConfirmSecurityAuthType } & Record<string, any>) {
    return this.post('/V4/UCenter/User/verifyEmailCode', data)
  }

  /**
   * 登录状态下获取谷歌验证密钥
   * @returns
   */
  getGoogleSecret() {
    return this.post<{ secret: string }>('/V4/UCenter/User/bindGoogleCode')
  }

  /**
   * 换绑谷歌
   * @param data
   * @returns
   */
  changeGoogle(data: BindOrChangeGoogleData) {
    return this.post('/V4/UCenter/User/changeGoogle', data)
  }

  /**
   * 绑定谷歌
   * @param data
   * @returns
   */
  bindGoogle(data: BindOrChangeGoogleData) {
    return this.post('/V4/UCenter/User/bindGoogle', data)
  }

  changeEmail(data: BindOrChangeEmailData) {
    return this.post('/V4/UCenter/User/changeEmail', data)
  }

  bindEmail(data: BindOrChangeEmailData) {
    return this.post('/V4/UCenter/User/bindEmail', data)
  }

  /**
   * 修改登录密码
   * @param data
   * @returns
   */
  updateLoginPassword(data: { oldPassword: string; password: string; type: CommonConfirmSecurityAuthType; tokenVerify: string }) {
    return this.post('/V4/UCenter/User/updatePassword', data)
  }

  /**
   * 验证支付密码
   * @param data
   * @returns
   */
  checkTradePwd(data: { tradePwd: string }) {
    return this.post<boolean>('/V4/UCenter/User/checkTradePwd', data)
  }

  /**
   * 重置支付密码
   * @param data
   * @returns
   */
  resetTradePassword(data: { tradePwd: string; confirmTradePwd: string; type: CommonConfirmSecurityAuthType; tokenVerify: string }) {
    return this.post('/V4/UCenter/User/resetTradePwd', data)
  }

  /**
   * 设置支付密码
   * @param data
   * @returns
   */
  setTradePassword(data: { tradePwd: string; confirmTradePwd: string }) {
    return this.post('/V4/UCenter/User/setTradePwd', data)
  }

  /**
   * 获取充值地址列表
   * @param data
   * @returns
   */
  getRechargeAddressList(data: { itemId: number; chainTagList: string }) {
    return this.post<ApiResponse.RechargeAddress[]>('/UCenter/Address/getFinanceAddressList', data)
  }

  /**
   * 获取充值,提币记录列表
   * @param data
   * @returns
   */
  getRechargeWithdrawRecordList(data: ApiRequest.PageBody & { itemId: ItemId; type: RechargeWithdrawRecordType }) {
    return this.post<HasPageInfoResponse<ApiResponse.RechargeWithdrawRecord>>('/UCenter/Assets/getCoinExchangeListV2', data)
  }

  /**
   * 修改地址
   * @param data
   * @returns
   */
  saveFinanceAddress(data: { uaId: number; bak: string }) {
    return this.post('/UCenter/Address/saveFinanceAddress', data)
  }

  /**
   * 创建地址
   * @param data
   * @returns
   */
  createFinanceAddress(data: { itemId: ItemId; chainTag: string; bak: string }) {
    return this.post<ApiResponse.RechargeAddress>('/UCenter/Address/createFinanceAddress', data)
  }

  /**
   * 删除地址
   * @param data
   * @returns
   */
  deleteFinanceAddress(data: { uaId: number }) {
    return this.post('/UCenter/Address/delFinanceAddress', data)
  }

  getWithdrawAddressList(data: { itemId: ItemId | undefined; addressType: WithdrawAddressType }) {
    return this.post<ApiResponse.WithdrawAddress[]>('/V4/UCenter/Address/addressList', data)
  }

  /**
   * 添加提币地址
   * @param data
   * @returns
   */
  addWithdrawAddress(data: AddWithdrawAddressData) {
    return this.post<ApiResponse.WithdrawAddress[]>('/UCenter/Address/addAddress', data)
  }

  /**
   * 删除提币地址
   * @param data
   * @returns
   */
  deleteWithdrawAddress(data: { uaId: number }) {
    return this.post('/UCenter/Address/delAddress', data)
  }
  /**
   * 修改提币地址
   * @param data
   * @returns
   */
  saveWithdrawAddress(data: { uaId: number; remark: string }) {
    return this.post('/V4/UCenter/Address/addressUpdate', data)
  }
  /**
   * 提币
   * @param data
   * @returns
   */
  swapAndOutTransfer(data: SwapAndOutTransferData) {
    return this.post<ApiResponse.WithdrawOrderInfo>('/V4/UCenter/Assets/swapAndOutTransfer', data, {
      isShowError: false,
    })
  }

  /** 检测提币 */
  checkSwapAndOutTransfer(data: CheckSwapAndOutTransferData) {
    return this.post<ApiResponse.CheckSwapAndOutTransfer>(
      '/V4/UCenter/Assets/checkSwapAndOutTransfer',
      {
        ...data,
        chainTag: data.chainTag || '',
        remark: data.remark || '',
        uecId: data.uecId || '',
        resultSccIds: data.resultSccIds || '',
      },
      {
        isShowError: false,
      },
    )
  }
  /**
   * 获取用户配置
   * @param data
   * @returns
   */
  getUserConfigs(
    data = {
      type: 'user_all_settings',
    },
  ) {
    return this.post<ApiResponse.UserConfigs>('/User/userItems', data)
  }

  /**
   * 提币额度 二十四小时提币数量
   * @param data
   * @returns
   */
  getUserExchangeLimit(data: { itemId: ItemId }) {
    return this.post<ApiResponse.UserExchangeLimit>('/UCenter/Users/getUserExchangeLimit', data)
  }

  getUserInfoByAddress(data: { address: string; transferType: WithdrawAddressType; uniqueId: string }) {
    return this.post<ApiResponse.UserInfoByAddress>('/UCenter/Address/getUserInfoByAddress', data, {
      isShowError: false,
    })
  }

  preCheckKyc(data: { itemId: ItemId; arriveAmount: CustomNumber }) {
    return this.post('/V4/UCenter/Assets/getlockAssets', data)
  }

  /**
   * 转账地址大额检测
   * @param data
   * @returns
   */
  preCheckAml(data: { itemId: ItemId; amount: CustomNumber; chainTag: string; address: string; transferType: WithdrawAddressType; area?: string }) {
    return this.post<string>('/V4/UCenter/Assets/transferCheck', data)
  }

  getTransferCheckData(data: { uecId: string }) {
    return this.post<ApiResponse.TransferCheckData>('/V4/UCenter/Assets/getTransferCheckData', data, {
      isShowError: false,
    })
  }

  withdrawCheckSafetyVerify(data: { tokenVerify: string; tokenWithdraw: string }) {
    return this.post<ApiResponse.CheckSwapAndOutTransfer>('/V4/UCenter/Assets/checkSafetyVerify', data)
  }

  /**
   * 检测支付密码是否匹配
   * @returns
   */
  withdrawCheckTradePwdVerifyToken(data: { tradePwd: string; tokenWithdraw: string }) {
    return this.post<ApiResponse.CheckSwapAndOutTransfer>('/V4/UCenter/Assets/checkTradePwdVerifyToken', data, {
      ignoreToCamelCaseFields: ['tradePwd'],
    })
  }

  getCoinOnChainTransferDetail(data: { uceId: number }) {
    return this.post<ApiResponse.ExchangeInfo>('/App/Assets/getExchangeInfo', data)
  }

  /** 检测手机是否存在 */
  registerCheckMobile(data: { area: string; mobile: string; inviteCode?: string; phone: string } & Partial<GeeTestResult>) {
    return this.post<ApiResponse.RegisterCheckResult>('/V4/UCenter/Register/checkMobile', data)
  }

  /** 检测邮箱是否存在 */
  registerCheckEmail(data: { email: string; inviteCode?: string } & Partial<GeeTestResult>) {
    return this.post<ApiResponse.RegisterCheckResult>('/V4/UCenter/Register/checkEmail', data)
  }

  /** 检测用户名是否存在 */
  registerCheckAccount(data: { username: string; inviteCode?: string } & Partial<GeeTestResult>) {
    return this.post<ApiResponse.RegisterCheckResult>('/V4/UCenter/Register/checkAccount', data)
  }

  /** 通过用户名注册 */
  registerByUsername(data: { username: string; pwd: string; tokenRegister: string; inviteCode?: string }) {
    return this.post<ApiResponse.LoginResponse>('/V4/UCenter/Register/account', data)
  }

  /** 通过手机号注册 */
  registerByMobile(data: { mobile: string; pwd: string; tokenRegister: string; area: string; inviteCode?: string }) {
    return this.post<ApiResponse.LoginResponse>('/V4/UCenter/Register/mobile', data)
  }

  /** 通过邮箱注册 */
  registerByEmail(data: { email: string; pwd: string; tokenRegister: string; inviteCode?: string }) {
    return this.post<ApiResponse.LoginResponse>('/V4/UCenter/Register/email', data)
  }

  /**
   * 检测手机验证码
   * @param data
   * @returns
   */
  registerCheckMobileCode(data: { mobile: string; area: string; mobileCode: string; tokenRegister: string }) {
    return this.post<ApiResponse.RegisterCheckResult>('/V4/UCenter/Register/checkMobileCode', data)
  }

  /**
   * 检测邮箱验证码
   * @param data
   * @returns
   */
  registerCheckEmailCode(data: { email: string; emailCode: string; tokenRegister: string }) {
    return this.post<ApiResponse.RegisterCheckResult>('/V4/UCenter/Register/checkEmailCode', data)
  }

  /**
   * 获取注册的邮箱验证码
   * @param data
   * @returns
   */
  registerGetEmailCode(data: { email: string; tokenRegister: string }) {
    return this.post<ApiResponse.RegisterCheckResult>('/V4/UCenter/Register/emailCode', data)
  }

  /**
   * 获取注册的手机验证码
   * @param data
   * @returns
   */
  registerGetMobileCode(data: { mobile: string; area: string; tokenRegister: string; phone?: string }) {
    return this.post<ApiResponse.RegisterCheckResult>('/V4/UCenter/Register/mobileCode', data)
  }

  /**
   * 检测登录名是否存在
   * @param data
   * @returns
   */
  loginCheckAccount(data: { area?: string; loginName: string } & Partial<GeeTestResult>) {
    return this.post<ApiResponse.LoginCheckResult>('/V4/UCenter/Login/checkLoginName', data)
  }

  /** 检测登录密码 */
  loginCheckPassword(data: { loginName: string; pwd: string; tokenLogin: string }) {
    return this.post<ApiResponse.LoginCheckResult>('/V4/UCenter/Login/checkPassword', data)
  }

  /** 检测安全验证 */
  loginCheckSafetyVerify(data: { tokenVerify: string; tokenLogin: string; loginName: string }) {
    return this.post<ApiResponse.LoginCheckResult>('/V4/UCenter/Login/checkSafetyVerify', data)
  }

  /** 找回密码 */
  findPwd(data: { loginName: string; tokenLogin: string }) {
    return this.post<ApiResponse.ForgetPasswordCheckResult>('/V4/UCenter/Forget/checkUniversal', data)
  }

  /**
   * 找回密码安全校验
   *
   * @param {{ token: string; tokenVerify: string; tokenForget: string }} data
   * @memberof UCenterRequest
   */
  forgetCheckSafetyVerify(data: { token: string; tokenVerify: string; tokenForget: string }) {
    return this.post<ApiResponse.ForgetPasswordCheckResult>('/V4/UCenter/Forget/checkSafetyVerify', data)
  }

  /**
   * 重置密码
   *
   * @param {{ token: string; tokenForget: string; pwd: string }} data
   * @memberof UCenterRequest
   */
  resetPwd(data: { token: string; tokenForget: string; pwd: string }) {
    return this.post('/V4/UCenter/Forget/resetPwd', data)
  }

  updateUserInfo(data: { nickName?: string; avatar?: string; intro?: string }) {
    const body: Record<string, string> = {}
    if (data.nickName) {
      body.nickName = data.nickName
    }
    if (data.avatar) {
      body.avatar = data.avatar
    }
    if (data.intro) {
      body.intro = data.intro
    }
    return this.post('/V4/UCenter/User/update', body)
  }

  uploadPicture(data: UploadPictureData) {
    return this.post<{ url: string }>('/V4/Common/Resource/upload', data)
  }

  /**
   * 获取通行密钥参数
   * @returns
   */
  openPassKeyArgs(data?: {}) {
    return this.post<ApiResponse.OpenPassKeyArgs>('/V4/UCenter/User/openPassKeyArgs', data)
  }

  openPassKey(data: { tokenVerify: string; passKey: string }) {
    return this.post<ApiResponse.OpenPassKeyArgs>('/V4/UCenter/User/openPassKey', {
      ...data,
      userVerification: 2,
      typeFormat: 6,
      phoneType: 'web',
    })
  }
}
/** 用户中心服务接口 */
export const UCenterServices = new UCenterRequest()

export enum RechargeWithdrawRecordStatus {
  Success = 'success',
  Waiting = 'waiting',
  Pending = 'pending',
  Canceled = 'canceled',
  Failed = 'failed',
  Confirming = 'confirming',
}

export const RechargeWithdrawRecordStatusMap = {
  [RechargeWithdrawRecordStatus.Success]: '完成',
  [RechargeWithdrawRecordStatus.Waiting]: '待确认',
  [RechargeWithdrawRecordStatus.Pending]: '待确认',
  [RechargeWithdrawRecordStatus.Canceled]: '取消',
  [RechargeWithdrawRecordStatus.Failed]: '失败',
  [RechargeWithdrawRecordStatus.Confirming]: '确认中',
}
